import { useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "react-router-dom"
import { formatDistanceToNow } from "date-fns"
import { ptBR } from "date-fns/locale"
import { Bell, Moon, Sun } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { ScrollArea } from "@/components/ui/scroll-area"
import {
  contarNaoLidas,
  listarNotificacoes,
  marcarNotificacaoLida,
  type Notificacao,
} from "@/lib/notificacoesApi"
import { useThemeStore } from "@/stores/sessionStore"

export function ThemeToggle() {
  const theme = useThemeStore((s) => s.theme)
  const setTheme = useThemeStore((s) => s.setTheme)
  const dark = theme === "dark" || (theme === "system" && useThemeStore.getState().resolvedTheme === "dark")
  return (
    <Button
      variant="ghost"
      size="icon"
      aria-label="Alternar tema"
      onClick={() => setTheme(dark ? "light" : "dark")}
    >
      {dark ? <Sun /> : <Moon />}
    </Button>
  )
}

export function NotificationsMenu() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [aberto, setAberto] = useState(false)

  const naoLidas = useQuery({
    queryKey: ["notificacoes", "nao-lidas"],
    queryFn: contarNaoLidas,
    refetchInterval: 60_000,
  })

  const lista = useQuery({
    queryKey: ["notificacoes", "lista"],
    queryFn: () => listarNotificacoes(0, 10),
    enabled: aberto,
    refetchInterval: aberto ? 30_000 : false,
  })

  const marcarLida = useMutation({
    mutationFn: marcarNotificacaoLida,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["notificacoes"] })
    },
  })

  const itens = lista.data?.content ?? []
  const total = naoLidas.data ?? 0

  function abrir(notificacao: Notificacao) {
    if (notificacao.status !== "LIDA") {
      marcarLida.mutate(notificacao.id)
    }
    if (notificacao.entidadeTipo === "FORMULARIO_RESPOSTA" && notificacao.entidadeId) {
      setAberto(false)
      navigate(`/formularios/respostas/${notificacao.entidadeId}`)
    }
  }

  return (
    <Popover open={aberto} onOpenChange={setAberto}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" aria-label="Notificações" className="relative">
          <Bell />
          {total > 0 ? (
            <span className="absolute -top-0.5 -right-0.5 flex size-4 items-center justify-center rounded-full bg-destructive text-[10px] font-semibold text-destructive-foreground">
              {total > 9 ? "9+" : total}
            </span>
          ) : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0">
        <div className="flex items-center justify-between border-b px-4 py-2.5">
          <span className="text-sm font-semibold">Notificações</span>
          {total > 0 ? <Badge variant="secondary">{total} não lida(s)</Badge> : null}
        </div>
        <ScrollArea className="h-80">
          {lista.isLoading ? (
            <p className="px-4 py-6 text-sm text-muted-foreground">Carregando…</p>
          ) : itens.length === 0 ? (
            <p className="px-4 py-6 text-sm text-muted-foreground">
              Nenhuma notificação por enquanto.
            </p>
          ) : (
            <ul className="divide-y">
              {itens.map((notificacao) => (
                <li key={notificacao.id}>
                  <button
                    type="button"
                    onClick={() => abrir(notificacao)}
                    className="flex w-full flex-col gap-0.5 px-4 py-3 text-left transition-colors hover:bg-muted/50"
                  >
                    <span className="flex items-center gap-2">
                      {notificacao.status !== "LIDA" ? (
                        <span className="size-1.5 shrink-0 rounded-full bg-primary" />
                      ) : null}
                      <span className="text-sm font-medium">{notificacao.titulo}</span>
                    </span>
                    {notificacao.corpo ? (
                      <span className="text-xs text-muted-foreground">{notificacao.corpo}</span>
                    ) : null}
                    <span className="text-[11px] text-muted-foreground">
                      {formatDistanceToNow(new Date(notificacao.criadaEm), {
                        addSuffix: true,
                        locale: ptBR,
                      })}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  )
}
